import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { Typography, TypographyProps } from './Typography';

const ANIMATION_DURATION_IN_MS = 300;

const flipStyle = css`
  display: block;
  position: relative;

  transition: height ${ANIMATION_DURATION_IN_MS}ms cubic-bezier(0.25, 1, 0.5, 1),
    width ${ANIMATION_DURATION_IN_MS}ms cubic-bezier(0.25, 1, 0.5, 1);
`;

const flipContainerStyle = css`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  > * {
    position: absolute;
    top: 0;
    margin: 0;
  }

  > :last-of-type:not(:first-of-type) {
    opacity: 0;
  }
`;

const flipCurrentAnimation = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(0%);
  }
`;

const flipPreviousAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0%);
  }
  100% {
    opacity: 1;
    transform: translateY(100%);
  }
`;

const flipAnimateStyle = css`
  > div:first-of-type {
    opacity: 0;
  }

  > .container {
    > .current {
      animation: ${flipCurrentAnimation} ${ANIMATION_DURATION_IN_MS}ms cubic-bezier(0.25, 1, 0.5, 1);
    }

    > .previous {
      animation: ${flipPreviousAnimation} ${ANIMATION_DURATION_IN_MS}ms cubic-bezier(0.25, 1, 0.5, 1);
    }
  }
`;

export const Flip: FC<TypographyProps> = ({ children, ...props }) => {
  const currentRef = useRef<HTMLParagraphElement>(null);
  const [current, setCurrent] = useState<ReactNode>(children);
  const [previous, setPrevious] = useState<ReactNode>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [heightUpdateIndex, setHeightUpdateIndex] = useState(1);

  const isPreviousSet = previous !== null && previous !== current;

  useEffect(() => {
    setCurrent((previous) => {
      setPrevious(previous);
      setHeightUpdateIndex((state) => state + 1);
      return children;
    });

    const animationEndTimeout = setTimeout(() => {
      setPrevious(null);
    }, ANIMATION_DURATION_IN_MS);

    return () => clearTimeout(animationEndTimeout);
  }, [children]);

  useEffect(() => {
    if (!currentRef.current) return;
    setHeight(currentRef.current.getBoundingClientRect().height);
  }, [heightUpdateIndex]);

  return (
    <span className={!height ? 'empty' : ''} css={[flipStyle, isPreviousSet && flipAnimateStyle]} style={height !== null ? { height } : {}}>
      <Typography ref={currentRef as any} {...props} component="div">
        {current}
      </Typography>
      {isPreviousSet && (
        <span css={flipContainerStyle} className="container" aria-hidden>
          <Typography {...(props as any)} component="div" className={`${props.className || ''} current`}>
            {current}
          </Typography>
          <Typography {...(props as any)} component="div" className={`${props.className || ''} previous`}>
            {previous}
          </Typography>
        </span>
      )}
    </span>
  );
};
